import React, { useMemo, useState, useEffect } from 'react';

import { Badge, Card, Spin } from 'antd';

import styled from 'styled-components';

import { modelFullName } from '@hooks/API/Contact/UseContactsList';
import { usePackageActions } from '@hooks/ModelsTable/usePackageActions';
import { usePackageDrawers } from '@hooks/ModelsTable/usePackageDrawers';
import usePlaceholderImageFixed from '@hooks/Utils/UsePlaceholderImageFixed';

import { BookSelectorDrawer } from '@components/BookSelectorForPackages';

import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ModelAvailabilityIcon from '@components/ContactsSelector/ModelsSelector/ModelsAvailability/ModelAvailabilityIcon';
import PackageActionIcons from '@components/ContactsSelector/ModelsSelector/ModelsWall/ModelsTable/PackageActionIcons';
import AvatarBg from '@components/Gui/AvatarBg';
import StyleVariables from '@components/Gui/StyleVariables';
import AddMediaDrawer from '@components/PackageDrawer/PackageModelCard/AddMediaDrawer/AddMediaDrawer';

import { StyledCardPackage } from './ModelCardUI';
import Title from 'antd/lib/typography/Title';

const BadgeWrapper = styled.div`
    position: relative;

    .ant-badge {
        width: 100%;
        height: 100%;

        .ant-badge-count {
            position: absolute;
            top: 8px;
            left: 8px;
            right: auto;
            transform: none;
            box-shadow: 0 0 0 1px #fff;
        }
    }
`;

const ModelCardPackage = ({ model }) => {
    const { additional, cache, context } = useContactsSelector();
    const { disabledItems } = additional;
    const { modelAvailability } = cache;

    const disabled = useMemo(() => disabledItems.includes(model.ID), [disabledItems, model.ID]);

    const [loading, setLoading] = useState(false);
    const { Meta } = Card;
    const { CG, ID, ST } = model;
    const [urlbg, isPlaceholder] = usePlaceholderImageFixed(CG, ST);

    const [isBusy, setIsBusy] = useState(false);
    const [isFetched, setFetched] = useState(false);
    const [newlyAddedToPackage, setNewlyAddedToPackage] = useState([]);

    const handleAddBooksToPackage = (modelId, bookIDs) => {
        const found = newlyAddedToPackage.find((item) => item.modelID === modelId);
        if (found) {
            const newPlaylistIDs = [...found.playlistIDs, ...bookIDs];
            const newModels = newlyAddedToPackage.map((item) => {
                if (item.modelID === modelId) {
                    return { ...item, playlistIDs: newPlaylistIDs };
                }
                return item;
            });
            setNewlyAddedToPackage(newModels);
            return;
        } else {
            setNewlyAddedToPackage([...newlyAddedToPackage, { modelID: modelId, playlistIDs: bookIDs }]);
        }
    };

    const handleRemoveBookFromPackage = (modelId, bookID) => {
        const found = newlyAddedToPackage.find((item) => item.modelID === modelId);
        if (found) {
            const newPlaylistIDs = found.playlistIDs.filter((id) => id !== bookID);
            if (newPlaylistIDs.length === 0) {
                const newModels = newlyAddedToPackage.filter((item) => item.modelID !== modelId);
                setNewlyAddedToPackage(newModels);
            } else {
                const newModels = newlyAddedToPackage.map((item) => {
                    if (item.modelID === modelId) {
                        return { ...item, playlistIDs: newPlaylistIDs };
                    }
                    return item;
                });
                setNewlyAddedToPackage(newModels);
            }
        }
    };

    // Use custom hooks for package actions and drawer management
    const packageActions = usePackageActions(
        model,
        context,
        newlyAddedToPackage,
        handleAddBooksToPackage,
        handleRemoveBookFromPackage
    );
    const drawerActions = usePackageDrawers();

    const {
        getModelPlaylists,
        getPlaylistCount,
        handleAddBook,
        handleDeleteBook,
        currentPackage,
        packageGroup,
        isOpenModelBooksDrawer,
        modelId,
        isMediaDrawer,
    } = { ...packageActions, ...drawerActions };

    const { handleCloseModelBooksDrawer, handleCloseAddMediaDrawer } = drawerActions;

    const playlistCount = getPlaylistCount(model);

    useEffect(() => {
        if (modelAvailability && modelAvailability.result) {
            const currentModelAvailability = modelAvailability.result
                ? modelAvailability.result.find((modelAv) => modelAv.ModelID === ID)
                : null;
            if (currentModelAvailability) {
                setIsBusy(true);
                setFetched(true);
            } else {
                if (modelAvailability && modelAvailability.result) {
                    setFetched(true);
                }
                setIsBusy(false);
            }
        }
    }, [modelAvailability]);

    return (
        <Spin spinning={loading}>
            <StyledCardPackage
                $status={isFetched ? (isBusy ? 'busy' : 'no-busy') : ''}
                hoverable
                cover={
                    <div className="model-card-top">
                        <div className="model-cover">
                            <BadgeWrapper>
                                <Badge count={playlistCount} color={StyleVariables.PrimaryColor}>
                                    <AvatarBg
                                        src={urlbg}
                                        isPlaceholder={isPlaceholder}
                                        height="13rem"
                                        width="100%"
                                        portrait
                                        disabled={disabled}
                                    />
                                </Badge>
                            </BadgeWrapper>
                        </div>
                    </div>
                }
                actions={
                    model.PL && [
                        <PackageActionIcons
                            key="package-actions"
                            model={model}
                            packageActions={packageActions}
                            drawerActions={drawerActions}
                            small={true}
                        />,
                    ]
                }
            >
                <Meta
                    title={
                        modelAvailability ? (
                            <div className="model-name-and-calendar">
                                <ModelAvailabilityIcon modelId={model.ID} isFetched={isFetched} isBusy={isBusy} />
                                <Title style={{ fontSize: '0.9rem', margin: 0 }}>
                                    {modelFullName(model, false, false)}
                                </Title>
                            </div>
                        ) : (
                            <Title style={{ fontSize: '0.9rem', margin: 0 }}>
                                {modelFullName(model, false, false)}
                            </Title>
                        )
                    }
                />
            </StyledCardPackage>
            {isOpenModelBooksDrawer && (
                <BookSelectorDrawer
                    contactID={modelId}
                    addedPlaylists={getModelPlaylists(modelId)}
                    packageGroup={packageGroup}
                    packageName={currentPackage.Name}
                    onAddBook={handleAddBook}
                    onRemoveBook={handleDeleteBook}
                    onClose={handleCloseModelBooksDrawer}
                />
            )}
            {isMediaDrawer && (
                <AddMediaDrawer
                    onClose={handleCloseAddMediaDrawer}
                    modelId={modelId}
                    packageId={currentPackage.ID}
                    packageGroup={packageGroup}
                />
            )}
        </Spin>
    );
};

export default ModelCardPackage;
